import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
  output,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { activitiesCompareFn } from '@wilson/activities/util';
import {
  GroupedUnassignedUserDataV2,
  MIN_UNASSIGNED_REGION_HEIGHT_PX,
  NumberOfShiftPerDayRecord,
  TimelineShift,
  UnassignedShift,
} from '@wilson/interfaces';
import { UnassignedShiftWithoutActivitiesOverlapLevelsPipe } from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
  getRawOffsetXFromEvent,
  SELECTED_REGION,
  TabEnum,
} from '@wilson/shift-timeline/services';
import {
  AddActivityToUnassignedRegion,
  SetDemarcatorRange,
  SetSelectedUnassignedRegion,
  ShiftTimelineDataState,
  ShiftTimelineFilterUnassignedRegionState,
  ShiftTimelineFilterUnassignedRegionStateFacadeService,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
  ShiftTimelineUnassignedRegionState,
  ShiftTimelineUnassignedRegionStateFacadeService,
  SetRegularViewUnassignedRegionHeightPx,
  TimelineCreateNewShiftV2,
  TimelineModalFilters,
  TimelineRemoveActivityFromShiftV2,
  TimelineRemoveShiftFromUserV2,
  TimelineUpdateShiftV2,
  TimelineUpdateUnassignedActivities,
  TimelineUpdateUnassignedRegionCollapsedViewShiftsCount,
  TimelineUpdateUnassignedShiftsRegionWithoutActivities,
  TimelineUpsertActivityV2,
  ToggleExpandUnassignedRegion,
  UnassignedRegionVisibleShiftsStateService,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { addDays, isValid, subDays } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
} from 'rxjs';
import { GroupAssignmentHelperService } from '../../../components-v2/group-assignment-confirmation-modal/group-assignment-helper.service';
import { UnassignShiftDropHelperService } from '../../../components-v2/unassign-shift-drop-helper/unassign-shift-drop-helper.service';
import { TimelineCommonOperationsService } from '../../../services/timeline-common-operations.service';
import { TimelineShiftAssignService } from '../../../services/timeline-shift-assign.service';
import { UnassignedRegionResizeService } from '../../../services/unassigned-region-resize.service';
import { UnassignShiftCommonOperationsService } from '../../../services/unassign-shift-common-operations.service';
import { ActivityDropZoneHelperService } from '../../activity-drop-zone/activity-drop-zone-helper.service';
import { NewShiftToUnassignedRegionDropHelperService } from '../new-shift-to-unassigned-region-drop-helper/new-shift-to-unassigned-region-drop-helper.service';

@Component({
  selector: 'wilson-regular-view-timeline-unassigned-region',
  templateUrl: './regular-view-timeline-unassigned-region.component.html',
  styleUrls: ['./regular-view-timeline-unassigned-region.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    UnassignedShiftWithoutActivitiesOverlapLevelsPipe,
    GroupAssignmentHelperService,
    UnassignShiftDropHelperService,
    TimelineShiftAssignService,
    NewShiftToUnassignedRegionDropHelperService,
    UnassignedRegionResizeService,
  ],
})
export class RegularViewTimelineUnassignedRegionComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private readonly store = inject(Store);
  protected readonly unassignedRegionResizeService = inject(
    UnassignedRegionResizeService,
  );
  private readonly shiftsService = inject(ShiftsService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  protected readonly activityDropZoneHelperService = inject(
    ActivityDropZoneHelperService,
  );
  private readonly unassignShiftCommonOperationsService = inject(
    UnassignShiftCommonOperationsService,
  );
  private readonly unassignShiftDropHelperService = inject(
    UnassignShiftDropHelperService,
  );
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly shiftTimelineFilterUnassignedRegionStateFacadeService =
    inject(ShiftTimelineFilterUnassignedRegionStateFacadeService);
  private readonly shiftTimelineUnassignedRegionStateFacadeService = inject(
    ShiftTimelineUnassignedRegionStateFacadeService,
  );
  private readonly unassignedRegionVisibleShiftsStateService = inject(
    UnassignedRegionVisibleShiftsStateService,
  );
  private readonly newShiftToUnassignedRegionDropHelperService = inject(
    NewShiftToUnassignedRegionDropHelperService,
  );

  private scrollLeftPxSubject = new BehaviorSubject<number>(0);

  @Input()
  set scrollLeftPx(value: number) {
    this.scrollLeftPxSubject.next(value);
  }

  @Output()
  scrolling = new EventEmitter<number>();

  readonly heightAppliedManually = output<void>();

  @ViewChild('rightContainerWrapper')
  rightContainerWrapper!: ElementRef;

  public resourcesColumnWidthPx$ = this.store.select(
    ShiftTimelineSettingsState.resourcesColumnWidthPx,
  );

  public frameLength$ = this.store.select(ShiftTimelineState.frameLength);

  isLoadingUnassignedShifts$!: Observable<boolean>;
  hideLoaderWhenLoadingUnassignedShiftsOrActivities$!: Observable<boolean>;
  isLoadingUnassignedActivities$!: Observable<boolean>;
  defaultRowHeight$!: Observable<number>;

  filters$!: Observable<TimelineModalFilters>;

  unassignedShiftsWithoutActivities$!: Observable<UnassignedShift[]>;
  filteredUnassignedShiftsWithoutActivities$!: Observable<UnassignedShift[]>;
  unassignedActivitiesViewRows$!: Observable<GroupedUnassignedUserDataV2[]>;
  filteredUnassignedActivitiesViewRows$!: Observable<
    GroupedUnassignedUserDataV2[]
  >;

  tabOptions = TabEnum;
  selectedTab$!: Observable<TabEnum>;
  maxHeight$!: Observable<number>;
  isLoadingUsers$!: Observable<boolean>;
  isRefreshingUnassignedShifts$!: Observable<boolean>;
  isRefreshingUnassignedActivities$!: Observable<boolean>;
  isHovered = false;
  isUnassignedRegionExpanded$!: Observable<boolean>;
  protected readonly isExpanding = signal(false);

  areActivitiesVisible$!: Observable<boolean>;

  private subscription = new Subscription();
  private tabSubscription!: Subscription;
  readonly faTrashAlt = faTrashAlt;

  protected pixelWidthPerDay$ = this.store.select(
    ShiftTimelineSettingsState.pixelWidthPerDay(),
  );
  protected numberOfShiftPerDayRecord$!: Observable<NumberOfShiftPerDayRecord>;

  protected visibleOverlapRows$!: Observable<{
    start: number;
    end: number;
  }>;
  protected offsetTopSubject = new BehaviorSubject<number>(0);

  ngOnInit(): void {
    this.unassignedRegionResizeService.setHeightAction(
      SetRegularViewUnassignedRegionHeightPx,
    );
    this.unassignedRegionResizeService.setToggleExpandedCallback((isExpanded) =>
      this.toggleRegion(isExpanded),
    );

    this.selectedTab$ = this.store.select(
      ShiftTimelineUnassignedRegionState.selectedRegion,
    );

    this.isUnassignedRegionExpanded$ = this.store.select(
      ShiftTimelineUnassignedRegionState.isUnassignedRegionExpanded,
    );

    this.subscription.add(
      combineLatest([
        this.timelineCommonOperationsService.createUnassignedRegionDataObservable(),
        this.selectedTab$,
        this.isUnassignedRegionExpanded$,
      ]).subscribe(([interval, selectedRegion, isUnassignedRegionExpanded]) => {
        if (isValid(interval.start) && isValid(interval.end)) {
          const payload = {
            start: subDays(interval.start, 1).getTime(),
            end: addDays(interval.end, 1).getTime(),
          };
          if (isUnassignedRegionExpanded) {
            const actionMap = {
              [TabEnum.UnassignedActivities]:
                new TimelineUpdateUnassignedActivities(payload),
              [TabEnum.UnassignedShifts]:
                new TimelineUpdateUnassignedShiftsRegionWithoutActivities(
                  payload,
                ),
            };

            const action = actionMap[selectedRegion];
            if (action) {
              try {
                this.store.dispatch(action);
              } catch (error) {
                console.error(error);
              }
            }
          } else {
            this.store.dispatch(
              new TimelineUpdateUnassignedRegionCollapsedViewShiftsCount(
                payload,
              ),
            );
          }
        }
      }),
    );

    this.numberOfShiftPerDayRecord$ = this.store.select(
      ShiftTimelineUnassignedRegionState.numberOfShiftPerDayRecords,
    );
    this.areActivitiesVisible$ = this.store.select(
      ShiftTimelineSettingsState.areActivitiesVisible,
    );

    this.isLoadingUnassignedShifts$ = this.store.select(
      ShiftTimelineUnassignedRegionState.isLoadingUnassignedShifts,
    );

    this.hideLoaderWhenLoadingUnassignedShiftsOrActivities$ = this.store.select(
      ShiftTimelineUnassignedRegionState.hideLoaderWhenLoadingUnassignedShiftsOrActivities,
    );

    this.isRefreshingUnassignedShifts$ = this.store.select(
      ShiftTimelineUnassignedRegionState.isRefreshingUnassignedShifts,
    );

    this.isRefreshingUnassignedActivities$ = this.store.select(
      ShiftTimelineUnassignedRegionState.isRefreshingUnassignedActivities,
    );

    this.isLoadingUnassignedActivities$ = this.store.select(
      ShiftTimelineUnassignedRegionState.isLoadingUnassignedActivities,
    );

    this.unassignedShiftsWithoutActivities$ =
      this.shiftTimelineUnassignedRegionStateFacadeService.unassignedShiftsWithoutActivities$;

    this.unassignedActivitiesViewRows$ = this.store.select(
      ShiftTimelineUnassignedRegionState.unassignedActivitiesViewRows,
    );

    this.filteredUnassignedActivitiesViewRows$ = this.store.select(
      ShiftTimelineFilterUnassignedRegionState.filteredUnassignedActivities(),
    );

    this.filteredUnassignedShiftsWithoutActivities$ =
      this.shiftTimelineFilterUnassignedRegionStateFacadeService.filteredUnassignedShiftsWithoutActivities$;

    this.maxHeight$ = this.store.select(
      ShiftTimelineDataState.maxUnassignedRowHeightPx,
    );

    this.defaultRowHeight$ = this.store.select(
      ShiftTimelineSettingsState.defaultRowHeight,
    );

    this.isLoadingUsers$ = this.store.select(
      ShiftTimelineDataState.isLoadingUsers,
    );

    this.visibleOverlapRows$ = combineLatest([
      this.maxHeight$,
      this.defaultRowHeight$,
      this.offsetTopSubject,
    ]).pipe(
      debounceTime(10),
      map(([maxHeight, defaultRowHeight, offsetTop]) => {
        const start = Math.floor(offsetTop / defaultRowHeight);
        const end = Math.ceil((offsetTop + maxHeight) / defaultRowHeight);

        return {
          start,
          end,
        };
      }),
    );

    this.tabSubscription = this.route.queryParamMap
      .pipe(
        debounceTime(10),
        map((data) => data.get(SELECTED_REGION)),
        distinctUntilChanged(),
      )
      .subscribe(async (selectedTab) => {
        const currentSelectedTabInState = await firstValueFrom(
          this.selectedTab$,
        );
        if (selectedTab && currentSelectedTabInState !== Number(selectedTab)) {
          this.store.dispatch(
            new SetSelectedUnassignedRegion(Number(selectedTab)),
          );
        }
      });
  }

  protected changeTabSelection(userSelectedTab: TabEnum): void {
    this.unassignShiftCommonOperationsService.changeTabSelection(
      userSelectedTab,
    );
    this.cdr.detectChanges();
  }

  dragover(event: DragEvent): void {
    event.preventDefault();
    if (!this.isHovered) this.isHovered = true;
  }

  dragleave(event: DragEvent): void {
    event.preventDefault();
    const isCursorOutsideDropZone =
      this.unassignShiftCommonOperationsService.isCursorOutsideDropZone(event);
    if (isCursorOutsideDropZone) {
      this.isHovered = false;
    }
  }

  async drop(event: DragEvent, selectedTab: TabEnum | null): Promise<void> {
    event.preventDefault();
    // Must run before any `await` in this handler — event.currentTarget is null afterward,
    // and dragend (fired on the drag source right after drop) resets dragStartOffsetX to null.
    const rawOffsetX = getRawOffsetXFromEvent(event);
    const dragStartOffsetX =
      this.createNewViaDragAndDropService.getDragStartOffsetX();
    const draggingItemId = await firstValueFrom(
      this.dragAndDropModeService.draggingItemId$,
    );

    if (draggingItemId === CreateNewViaDragAndDropService.MOCK_SHIFT_ID) {
      await this.newShiftToUnassignedRegionDropHelperService.createShiftFromRegularViewDrop(
        rawOffsetX,
        dragStartOffsetX,
      );
    } else if (selectedTab === TabEnum.UnassignedActivities) {
      await this.handleActivityDrop();
    } else if (selectedTab === TabEnum.UnassignedShifts) {
      await this.handleShiftDrop();
    }
    this.isHovered = false;
    this.cdr.detectChanges();
  }

  private async handleShiftDrop(): Promise<void> {
    const draggingShift = await firstValueFrom(
      this.dragAndDropModeService.draggingShiftV2$,
    );

    if (!draggingShift?.userId) {
      return;
    }

    await this.unassignShiftDropHelperService.handleStandardViewShiftDrop(
      draggingShift,
      () => {
        firstValueFrom(this.isUnassignedRegionExpanded$).then((isExpanded) => {
          if (!isExpanded) {
            this.timelineCommonOperationsService.refreshUnassignedRegion();
          }
        });
      },
    );
  }

  updateScrollingPosition(event: Event): void {
    if (!this.isExpanding()) {
      this.scrolling.emit((event.target as HTMLElement).scrollLeft);
      this.offsetTopSubject.next((event.target as HTMLElement).scrollTop);
    }
  }

  ngOnDestroy(): void {
    // Flush to prevent memory leak
    this.isLoadingUnassignedShifts$ = null as any;
    this.hideLoaderWhenLoadingUnassignedShiftsOrActivities$ = null as any;
    this.isLoadingUnassignedActivities$ = null as any;
    this.defaultRowHeight$ = null as any;
    this.scrollLeftPxSubject = null as any;
    this.visibleOverlapRows$ = null as any;

    this.subscription?.unsubscribe();
    this.tabSubscription?.unsubscribe();
    this.unassignedRegionVisibleShiftsStateService.clearVisibleShiftIdsSet();
  }

  toggleRegion(isOpened: boolean): void {
    this.store.dispatch(new ToggleExpandUnassignedRegion(isOpened));
    this.isExpanding.set(true);

    setTimeout(() => {
      this.isExpanding.set(false);
    }, 100);
  }

  protected manualToggleRegion(isOpened: boolean): void {
    this.toggleRegion(isOpened);
    const heightPx = isOpened
      ? this.store.selectSnapshot(
          ShiftTimelineDataState.maxUnassignedRowHeightPx,
        )
      : MIN_UNASSIGNED_REGION_HEIGHT_PX;
    this.unassignedRegionResizeService.applyHeightPx(heightPx);
    this.heightAppliedManually.emit();
  }

  private async handleActivityDrop(): Promise<void> {
    const activity = await firstValueFrom(
      this.activityDropZoneHelperService.activity$,
    );

    if (activity?.shiftId) {
      const previousShift = this.store.selectSnapshot(
        ShiftTimelineDataState.getShiftWithoutActivities(activity.shiftId),
      );
      if (previousShift?.userId) {
        let shiftActivities = this.store.selectSnapshot(
          ShiftTimelineDataState.shiftActivitiesArray(activity.shiftId),
        );
        try {
          this.store.dispatch(new SetDemarcatorRange([]));
          this.dragAndDropModeService.setActivityDropZone(false);
          this.dragAndDropModeService.setMoveActivityDropZone(false);
          this.dragAndDropModeService.setDraggingItemId(null);
          this.store.dispatch(
            new TimelineRemoveActivityFromShiftV2(
              activity.id as string,
              previousShift.id,
            ),
          );
          if (shiftActivities) {
            shiftActivities = shiftActivities.filter(
              (a) => a.id !== activity.id,
            );
            if (shiftActivities.length) {
              shiftActivities.sort(activitiesCompareFn);
              const updatedShift: TimelineShift = {
                ...previousShift,
                startDatetime: shiftActivities[0].startDatetime,
                startLocationId: shiftActivities[0].startLocationId as string,
                startLocation: {
                  locationCode:
                    shiftActivities[0].startLocation?.locationCode || '',
                  name: shiftActivities[0].startLocation?.name || '',
                },
                endDatetime:
                  shiftActivities[shiftActivities.length - 1].endDatetime,
                endLocationId: shiftActivities[0].endLocationId as string,
                endLocation: {
                  locationCode:
                    shiftActivities[0].endLocation?.locationCode || '',
                  name: shiftActivities[0].endLocation?.name || '',
                },
              };
              this.store.dispatch(new TimelineUpdateShiftV2(updatedShift));
              await this.shiftsService.update({
                ...previousShift,
                activities: shiftActivities,
              });
              this.timelineCommonOperationsService.updateUsersAssignmentsState([
                previousShift.userId,
              ]);
            } else {
              this.store.dispatch(
                new TimelineRemoveShiftFromUserV2({
                  shiftId: previousShift.id,
                  userId: previousShift.userId,
                }),
              );
              await this.shiftsService.deleteShift(previousShift.id);
            }
          }
          if (activity?.serviceId) {
            const activityToUnAssign = { ...activity };
            activityToUnAssign.shiftId = null;
            this.store.dispatch([
              new AddActivityToUnassignedRegion(activityToUnAssign),
              new SetDemarcatorRange([]),
            ]);
          }
        } catch {
          this.store.dispatch(
            new TimelineCreateNewShiftV2(previousShift, previousShift.userId),
          );
          this.store.dispatch(
            new TimelineUpsertActivityV2([activity], activity.shiftId),
          );
          this.nzMessageService.error(
            this.translateService.instant('general.something_went_wrong'),
          );
        }
      }
    }
  }

  ngAfterViewInit(): void {
    this.unassignedRegionResizeService.setExtraSizedElements([
      {
        element: this.rightContainerWrapper.nativeElement,
        property: 'max-height',
      },
    ]);
    const isExpanded = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.isUnassignedRegionExpanded,
    );
    this.unassignedRegionResizeService.applyHeightPx(
      isExpanded
        ? this.store.selectSnapshot(
            ShiftTimelineDataState.maxUnassignedRowHeightPx,
          )
        : MIN_UNASSIGNED_REGION_HEIGHT_PX,
    );

    this.subscription.add(
      this.scrollLeftPxSubject.subscribe((scrollLeft: number) => {
        this.rightContainerWrapper.nativeElement.scrollLeft = scrollLeft;
      }),
    );
  }
}
